// ============================================
// ApproveAllButton — UKA-X01 D6
//
// Renders only for formulary_supplement category sections.
// Shows "Approve All Supplements in This Group" — triggers batch approve
// for all IN_REVIEW items in the group with a single confirmation modal.
//
// Design: DM Sans, no framer-motion, pure CSS transitions.
// Palette: BG #FAFAFA, Card #FFFFFF, Text #18181B, Rose #E11D48
// ============================================

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  collection,
  addDoc,
  serverTimestamp,
  writeBatch,
  doc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getSessionId, getSessionIpAddress, isAuthenticated } from '../lib/auth';
import type { ClinicalReviewItem } from '../../data/portal/schema';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ApproveAllItem {
  contentId: string;
  item: ClinicalReviewItem;
}

export interface ApproveAllButtonProps {
  /** All items in the current group being displayed. Only IN_REVIEW formulary_supplement items will be batch-approved. */
  items: ApproveAllItem[];
  /** Firestore tenant ID (e.g. "hemologix") */
  tenantId: string;
  /** Called with the count of successfully approved items after batch completes. */
  onSuccess?: (approvedCount: number) => void;
}

// ── SHA-256 helper (Web Crypto API) ───────────────────────────────────────────

async function computeBodyHash(body: unknown): Promise<string> {
  const encoded = new TextEncoder().encode(JSON.stringify(body));
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── ApproveAllButton ──────────────────────────────────────────────────────────

/**
 * Visible only when at least one formulary_supplement item with status IN_REVIEW
 * exists in the items array. Hidden for all other content types.
 */
export function ApproveAllButton({
  items,
  tenantId,
  onSuccess,
}: ApproveAllButtonProps): React.ReactElement | null {
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<number | null>(null); // approved count
  const modalRef = useRef<HTMLDivElement>(null);

  // Filter to only formulary_supplement items that are IN_REVIEW
  const eligibleItems = items.filter(
    ({ item }) =>
      item.type === 'formulary_supplement' && item.status === 'IN_REVIEW',
  );

  // Do not render for non-supplement content types or when nothing is eligible
  if (eligibleItems.length === 0) return null;

  // Trap focus and handle Escape in the modal
  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  const handleOpen = useCallback(() => {
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    if (!submitting) setModalOpen(false);
  }, [submitting]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    if (!isAuthenticated()) {
      setError('Not authenticated. Please sign in again.');
      setSubmitting(false);
      return;
    }

    const sessionId = getSessionId() ?? 'unknown-session';
    const ipAddress = await getSessionIpAddress(sessionId);

    try {
      // Firestore writeBatch for atomicity (max 500 ops; 29 supplements is well within limit)
      const batch = writeBatch(db);

      // Process each eligible item
      const verdictWrites: Promise<void>[] = eligibleItems.map(async ({ contentId, item }) => {
        const contentHashAtReview = await computeBodyHash(item.body);

        const verdictRef = doc(
          collection(db, 'tenants', tenantId, 'content', contentId, 'verdicts'),
        );

        batch.set(verdictRef, {
          action: 'APPROVE',
          actor: sessionId,
          actorEmail: 'portal-reviewer',
          actorDisplayName: 'Portal Reviewer',
          // Server timestamp — never client-supplied
          timestamp: serverTimestamp(),
          contentVersion: item.version,
          // SHA-256 of body at verdict time — forensic chain of custody
          contentHash: contentHashAtReview,
          comment: 'Batch approved via Approve All — low-risk formulary supplement group.',
          clinicalJustification: null,
          // IP captured server-side at capturePortalLoginSession (D2 auth), read from portal_sessions
          ipAddress,
          userAgent: navigator.userAgent,
          sessionId,
          previousVerdictHash: null,
        });
      });

      // Wait for all hash computations to complete before committing batch
      await Promise.all(verdictWrites);
      await batch.commit();

      setSuccess(eligibleItems.length);
      onSuccess?.(eligibleItems.length);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Batch approval failed: ${msg}. No verdicts were saved.`);
    } finally {
      setSubmitting(false);
    }
  }, [eligibleItems, tenantId, onSuccess]);

  if (success !== null) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded bg-green-50 border border-green-200 text-sm text-green-800">
        <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {success} supplement{success !== 1 ? 's' : ''} approved.
      </div>
    );
  }

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Approve All Supplements in This Group
        <span className="ml-1 text-green-100 font-normal">({eligibleItems.length})</span>
      </button>

      {/* Confirmation modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="approve-all-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={handleCancel}
            aria-hidden="true"
          />

          {/* Modal card */}
          <div
            ref={modalRef}
            className="relative bg-white rounded border border-zinc-200 shadow-lg w-full max-w-md"
          >
            {/* Header */}
            <div className="px-6 pt-5 pb-4 border-b border-zinc-100">
              <h2 id="approve-all-title" className="text-base font-semibold text-[#18181B]">
                Approve All Supplements
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                This will submit an Approve verdict for {eligibleItems.length} supplement
                {eligibleItems.length !== 1 ? 's' : ''} simultaneously.
              </p>
            </div>

            {/* Item list */}
            <div className="px-6 py-4 max-h-48 overflow-y-auto">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                Items to be approved
              </p>
              <ul className="space-y-1">
                {eligibleItems.map(({ contentId, item }) => (
                  <li key={contentId} className="flex items-center gap-2 text-sm text-zinc-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                    {item.title}
                  </li>
                ))}
              </ul>
            </div>

            {/* Conditions */}
            <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-100 border-b">
              <ul className="space-y-1 text-xs text-zinc-500">
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-zinc-400">•</span>
                  Only applies to low-risk Tier 1 formulary supplements.
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-zinc-400">•</span>
                  All {eligibleItems.length} verdicts will be immutable audit records under your name.
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-zinc-400">•</span>
                  Items flagged off-label or controversial are not eligible for batch approval
                  and will not appear above if present.
                </li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-100">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="px-4 py-2 rounded text-sm font-medium text-zinc-600 bg-white border border-zinc-300 hover:bg-zinc-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:ring-offset-1 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                className="px-5 py-2 rounded text-sm font-semibold text-white bg-green-600 border border-green-600 hover:bg-green-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? `Approving ${eligibleItems.length}...`
                  : `Confirm — Approve All ${eligibleItems.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
